import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { useSchedulerV3QueryUpdaters } from '../query-updaters';

dayjs.extend(utc);
dayjs.extend(isBetween);

export const useSchedulerV3InvalidateQueries = () => {
  const { invalidateQueries } = useSchedulerV3QueryUpdaters();

  const invalidateGetAppointmentType = (appointmentTypeId: string) =>
    invalidateQueries({
      endpointName: 'GetAppointmentType',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return request.appointmentTypeId === appointmentTypeId;
        },
      },
    });

  const invalidateListAppointmentTypes = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListAppointmentTypes',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListAppointmentTypesMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidateListProviders = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListProviders',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListProvidersMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidGetProvider = (providerId: string) =>
    invalidateQueries({
      endpointName: 'GetProvider',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => queryKey[2].id === providerId,
      },
    });

  const invalidateListMappings = (locationId?: string) =>
    invalidateQueries({
      endpointName: 'ListMappings',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationIds ?? []).includes(locationId);
        },
      },
    });

  // entityIds is an array of locationIds or providerIds
  const invalidateListSchedules = (entityIds: string[]) =>
    invalidateQueries({
      endpointName: 'ListSchedules',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !!request.ids?.some((entityId) => entityIds?.includes(entityId));
        },
      },
    });

  const invalidateListBookingSubmissions = (locationId?: string) =>
    invalidateQueries({
      endpointName: 'ListBookingSubmissions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });

  const invalidateGetBookingSubmission = (bookingSubmissionId?: string) =>
    invalidateQueries({
      endpointName: 'GetBookingSubmission',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => !!bookingSubmissionId || queryKey[2].submissionId === bookingSubmissionId,
      },
    });

  const invalidateListCalendarEvents = (locationId?: string, startDateTime?: string) => {
    invalidateQueries({
      endpointName: 'ListCalendarEvents',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return (
            (!locationId || request.locationId === locationId) &&
            (!startDateTime ||
              dayjs
                .utc(startDateTime)
                .isBetween(dayjs.utc(request.startDateTime), dayjs.utc(request.endDateTime), null, '[]'))
          );
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListCalendarEventsMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return (
            (!locationId || (request.locationsIds ?? []).includes(locationId)) &&
            (!startDateTime ||
              dayjs
                .utc(startDateTime)
                .isBetween(dayjs.utc(request.startDateTime), dayjs.utc(request.endDateTime), null, '[]'))
          );
        },
      },
    });
  };

  const invalidateGetCalendarEvent = (eventId?: string) =>
    invalidateQueries({
      endpointName: 'GetCalendarEvent',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !eventId || request.eventId === eventId;
        },
      },
    });

  const invalidateListOperatories = (locationId?: string) => {
    invalidateQueries({
      endpointName: 'ListOperatories',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || request.locationId === locationId;
        },
      },
    });

    invalidateQueries({
      endpointName: 'ListOperatoriesMulti',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !locationId || (request.locationsIds ?? []).includes(locationId);
        },
      },
    });
  };

  const invalidateGetOperatory = (operatoryId?: string) =>
    invalidateQueries({
      endpointName: 'GetOperatory',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const request = queryKey[2];
          return !operatoryId || request.operatoryId === operatoryId;
        },
      },
    });

  const invalidateBookingSubmissions = (locationIds: string[]) => {
    invalidateQueries({
      endpointName: 'ListBookingSubmissions',
      queryFilters: {
        exact: false,
        predicate: ({ queryKey }) => {
          const queryKeyLocationIds = queryKey[2].locationsIds || [];
          return queryKeyLocationIds.some((id) => locationIds.includes(id));
        },
      },
    });
  };

  return {
    invalidateListAppointmentTypes,
    invalidateGetAppointmentType,
    invalidateListProviders,
    invalidGetProvider,
    invalidateListMappings,
    invalidateListSchedules,
    invalidateListBookingSubmissions,
    invalidateGetBookingSubmission,
    invalidateListCalendarEvents,
    invalidateGetCalendarEvent,
    invalidateListOperatories,
    invalidateGetOperatory,
    invalidateBookingSubmissions,
  };
};
